import md5 from "md5";
import { type KeyValueItem } from "@/lib/@types/base";
import { type LanguageCodeEnum } from "@/gql/graphql";
import { type ProductApiType } from "@/lib/@types/api/product";
import { message } from "antd";
// 获取当前是哪个频道

export const i18nKey = (str: string): string => {
	if (!str) return str;
	return md5(str);
};

// 生成一个随机uuid
export const generateUuid = () => {
	return md5(
		"xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
			let r = (Math.random() * 16) | 0,
				v = c == "x" ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		}),
	);
};

// 是否为开发环境
export const isDev = process.env.NODE_ENV === "development";
//获取当前域名
export const cApiUrl = isDev ? process.env.NEXT_PUBLIC_DEV_URL : process.env.NEXT_PUBLIC_SITE_URL;

// 防抖函数
export function debounce(func: Function, delay: number): Function {
	let timer: NodeJS.Timeout;

	return function (this: any, ...args: any[]) {
		const context = this;

		clearTimeout(timer);
		timer = setTimeout(() => {
			func.apply(context, args);
		}, delay);
	};
}

// 剔除builder服务端组件传递给客户端组件的某些值会出现报错

// 将 locale “-” 转成"_" 全部大写 请求多语言数据时需要处理
export const handleGraphqlLocale = (locale: string) => {
	return locale.replace("-", "_").toUpperCase() as LanguageCodeEnum;
};

export const formatMoney = (amount: number, currency: string) =>
	new Intl.NumberFormat("en-US", {
		style: "currency",
		currency,
	}).format(amount);

export const formatMoneyRange = (
	range: {
		start?: { amount: number; currency: string } | null;
		stop?: { amount: number; currency: string } | null;
	} | null,
) => {
	const { start, stop } = range || {};
	const startMoney = start && formatMoney(start.amount, start.currency);
	const stopMoney = stop && formatMoney(stop.amount, stop.currency);

	if (startMoney === stopMoney) {
		return startMoney;
	}

	return `${startMoney} - ${stopMoney}`;
};

export const showI18nName = (props: any, key = "name") => {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	return props?.translations?.[key] || props?.[key] || "";
};

// 判断是否是一个json
export const isJson = (str: string) => {
	try {
		JSON.parse(str);
	} catch (e) {
		return false;
	}
	return true;
};

export const handleMediaMetadata = (metadata: KeyValueItem[] | undefined) => {
	if (!metadata || !metadata?.length) return undefined;
	const meta = metadata.find((item) => item.key === "media");
	if (!meta || !isJson(meta.value)) return undefined;
	return {
		media: JSON.parse(meta.value) as ProductApiType.MediaMetadata[],
	};
};

export const handleMediaMp4Metadata = (metadata: KeyValueItem[] | undefined) => {
	if (!metadata || !metadata?.length) return undefined;
	const meta = metadata.find((item) => item.key === "cover_mp4");
	if (!meta || !isJson(meta.value)) return undefined;
	return {
		mp4: JSON.parse(meta.value) as {
			id: string;
			name: string;
			url: string;
		}[],
	};
};

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export type ObserveElementFunction = (target: IntersectionObserverEntry["target"]) => void;

export type BaseAxiosResponse<D extends any> = {
	code: number;
	data: D;
	message: string;
};

export function observeElementIntersection(
	className: string,
	enter: ObserveElementFunction,
	leave?: ObserveElementFunction,
) {
	const elements = document.querySelectorAll(`${className}`);

	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				enter(entry.target);
			} else {
				leave && leave(entry.target);
			}
		});
	});

	elements.forEach((element) => {
		observer.observe(element);
	});
}

// 设置html的overflow
export function setOverflow(overflow: string) {
	document.documentElement.style.overflow = overflow;
}

// 地区
export function getRegionInfo(countryCode: string) {
	const postalCodeMap: { [key: string]: string } = {
		// 非洲
		AO: "1000", // 安哥拉
		BF: "01", // 布基纳法索
		BI: "100", // 布隆迪
		BJ: "01", // 贝宁
		BW: "0100", // 博茨瓦纳
		CD: "1000", // 刚果民主共和国
		CF: "100", // 中非共和国
		CG: "100", // 刚果共和国
		CI: "01", // 科特迪瓦
		CM: "100", // 喀麦隆
		CV: "1000", // 佛得角
		DJ: "100", // 吉布提
		DZ: "16000", // 阿尔及利亚
		EG: "11511", // 埃及
		EH: "70000", // 西撒哈拉
		ER: "100", // 厄立特里亚
		ET: "1000", // 埃塞俄比亚
		GA: "100", // 加蓬
		GH: "100", // 加纳
		GM: "1000", // 冈比亚
		GN: "100", // 几内亚
		GQ: "100", // 赤道几内亚
		GW: "1000", // 几内亚比绍
		KE: "00100", // 肯尼亚
		KM: "100", // 科摩罗
		LR: "1000", // 利比里亚
		LS: "100", // 莱索托
		LY: "100", // 利比亚
		MA: "10000", // 摩洛哥
		MG: "101", // 马达加斯加
		ML: "100", // 马里
		MR: "100", // 毛里塔尼亚
		MU: "100", // 毛里求斯
		MW: "100", // 马拉维
		MZ: "1000", // 莫桑比克
		NA: "10000", // 纳米比亚
		NE: "100", // 尼日尔
		NG: "100000", // 尼日利亚
		RE: "97400", // 留尼汪
		RW: "100", // 卢旺达
		SC: "100", // 塞舌尔
		SD: "11111", // 苏丹
		SL: "100", // 塞拉利昂
		SN: "10000", // 塞内加尔
		SO: "100", // 索马里
		SS: "100", // 南苏丹
		ST: "100", // 圣多美和普林西比
		SZ: "100", // 斯威士兰
		TD: "100", // 乍得
		TG: "100", // 多哥
		TN: "1000", // 突尼斯
		TZ: "100", // 坦桑尼亚
		UG: "100", // 乌干达
		ZA: "0002", // 南非
		ZM: "10101", // 赞比亚
		ZW: "100", // 津巴布韦

		// 亚洲
		AE: "00000", // 阿联酋
		AF: "1001", // 阿富汗
		AM: "0010", // 亚美尼亚
		AZ: "1000", // 阿塞拜疆
		BD: "1000", // 孟加拉国
		BH: "100", // 巴林
		BN: "BE1234", // 文莱
		BT: "10001", // 不丹
		CN: "100000", // 中国
		CY: "1000", // 塞浦路斯
		GE: "0100", // 格鲁吉亚
		HK: "999077", // 香港
		ID: "10110", // 印度尼西亚
		IL: "9100000", // 以色列
		IN: "110001", // 印度
		IQ: "10001", // 伊拉克
		IR: "1234567890", // 伊朗
		JO: "11937", // 约旦
		JP: "100-0001", // 日本
		KG: "720001", // 吉尔吉斯斯坦
		KH: "12000", // 柬埔寨
		KP: "100-000", // 朝鲜
		KR: "03051", // 韩国
		KW: "12345", // 科威特
		KZ: "010000", // 哈萨克斯坦
		LA: "01000", // 老挝
		LB: "1100", // 黎巴嫩
		LK: "10100", // 斯里兰卡
		MM: "11181", // 缅甸
		MN: "14201", // 蒙古
		MO: "999078", // 澳门
		MV: "20026", // 马尔代夫
		MY: "50450", // 马来西亚
		NP: "44600", // 尼泊尔
		OM: "100", // 阿曼
		PH: "1000", // 菲律宾
		PK: "44000", // 巴基斯坦
		PS: "100", // 巴勒斯坦
		QA: "100", // 卡塔尔
		SA: "11564", // 沙特阿拉伯
		SG: "238858", // 新加坡
		SY: "100", // 叙利亚
		TH: "10200", // 泰国
		TJ: "100", // 塔吉克斯坦
		TL: "100", // 东帝汶
		TM: "100", // 土库曼斯坦
		TW: "100", // 台湾
		UZ: "100000", // 乌兹别克斯坦
		VN: "100000", // 越南
		YE: "100", // 也门

		// 欧洲
		AD: "AD100", // 安道尔
		AL: "1001", // 阿尔巴尼亚
		AT: "1010", // 奥地利
		BA: "71000", // 波斯尼亚和黑塞哥维那
		BE: "1000", // 比利时
		BG: "1000", // 保加利亚
		BY: "220000", // 白俄罗斯
		CH: "3000", // 瑞士
		CZ: "100 00", // 捷克
		DE: "10115", // 德国
		DK: "1000", // 丹麦
		EE: "10111", // 爱沙尼亚
		ES: "28001", // 西班牙
		FI: "00100", // 芬兰
		FR: "75001", // 法国
		GB: "SW1A 1AA", // 英国
		GR: "104 31", // 希腊
		HR: "10000", // 克罗地亚
		HU: "1011", // 匈牙利
		IE: "D01 F5P2", // 爱尔兰
		IS: "101", // 冰岛
		IT: "00100", // 意大利
		LI: "9490", // 列支敦士登
		LT: "01001", // 立陶宛
		LU: "1009", // 卢森堡
		LV: "LV-1050", // 拉脱维亚
		MC: "98000", // 摩纳哥
		MD: "2012", // 摩尔多瓦
		ME: "81000", // 黑山
		MK: "1000", // 北马其顿
		MT: "VLT 1117", // 马耳他
		NL: "1011 AC", // 荷兰
		NO: "0010", // 挪威
		PL: "00-001", // 波兰
		PT: "1000-001", // 葡萄牙
		RO: "010001", // 罗马尼亚
		RS: "11000", // 塞尔维亚
		RU: "101000", // 俄罗斯
		SE: "111 52", // 瑞典
		SI: "1000", // 斯洛文尼亚
		SK: "811 01", // 斯洛伐克
		SM: "47890", // 圣马力诺
		TR: "06100", // 土耳其
		UA: "01001", // 乌克兰
		VA: "00120", // 梵蒂冈城

		// 北美洲
		AG: "100", // 安提瓜和巴布达
		BB: "BB11000", // 巴巴多斯
		BM: "HM 12", // 百慕大
		BS: "100", // 巴哈马
		BZ: "100", // 伯利兹
		CA: "K1A 0A1", // 加拿大
		CR: "10101", // 哥斯达黎加
		CU: "10100", // 古巴
		DM: "100", // 多米尼克
		DO: "10101", // 多米尼加共和国
		GD: "100", // 格林纳达
		GT: "01001", // 危地马拉
		HN: "11101", // 洪都拉斯
		HT: "6110", // 海地
		JM: "100", // 牙买加
		KN: "100", // 圣基茨和尼维斯
		LC: "100", // 圣卢西亚
		MX: "01000", // 墨西哥
		NI: "11001", // 尼加拉瓜
		PA: "100", // 巴拿马
		PR: "00901", // 波多黎各
		SV: "1101", // 萨尔瓦多
		TT: "100", // 特立尼达和多巴哥
		US: "10001", // 美国
		VC: "100", // 圣文森特和格林纳丁斯

		// 南美洲
		AR: "C1000AAA", // 阿根廷
		BO: "1000", // 玻利维亚
		BR: "01000-000", // 巴西
		CL: "8320000", // 智利
		CO: "110111", // 哥伦比亚
		EC: "170515", // 厄瓜多尔
		GY: "100", // 圭亚那
		PE: "15001", // 秘鲁
		PY: "1000", // 巴拉圭
		SR: "100", // 苏里南
		UY: "11000", // 乌拉圭
		VE: "1010", // 委内瑞拉

		// 大洋洲
		AU: "2000", // 澳大利亚
		FJ: "100", // 斐济
		FM: "96941", // 密克罗尼西亚
		KI: "100", // 基里巴斯
		MH: "96960", // 马绍尔群岛
		NR: "100", // 瑙鲁
		NZ: "1010", // 新西兰
		PG: "100", // 巴布亚新几内亚
		PW: "96940", // 帕劳
		SB: "100", // 所罗门群岛
		TO: "100", // 汤加
		TV: "100", // 图瓦卢
		VU: "100", // 瓦努阿图
		WS: "100", // 萨摩亚
	};

	return postalCodeMap[countryCode.toUpperCase()] || "";
}

function escapeXml(unsafe: string) {
	return unsafe.replace(/[<>&'"]/g, function (c) {
		switch (c) {
			case "<":
				return "&lt;";
			case ">":
				return "&gt;";
			case "&":
				return "&amp;";
			case "'":
				return "&apos;";
			case '"':
				return "&quot;";
			default:
				return c;
		}
	});
}

export const createSitemap = (urls: string[]): string => {
	return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:news="http://www.google.com/schemas/sitemap-news/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml" xmlns:mobile="http://www.google.com/schemas/sitemap-mobile/1.0" xmlns:image="http://www.google.com/schemas/sitemap-image/1.1" xmlns:video="http://www.google.com/schemas/sitemap-video/1.1">
  ${urls
		.map(
			(url) => `
    <url>
      <loc>${escapeXml(url)}</loc>
      <lastmod>${new Date().toISOString()}</lastmod>
      <changefreq>daily</changefreq>
      <priority>0.7</priority>
    </url>`,
		)
		.join("\n")}
</urlset>`;
};

export const createLanguageSitemap = (urls: string[]): string => {
	return `<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  ${urls
		.map(
			(url) => `
    <sitemap>
      <loc>${escapeXml(url)}</loc>
    </sitemap>`,
		)
		.join("\n")}
</sitemapindex>`;
};

export const defaultLocale = "en";

export type GetSeoMenusResponse = {
	id: number | string;
	link: string;
	name: string;
	flag: number;
	isEditPage: boolean;
};

export const generateLink = (link: string) => {
	if (link.startsWith("/")) {
		return `${process.env.NEXT_PUBLIC_SITE_URL}${link}`;
	} else {
		return `${process.env.NEXT_PUBLIC_SITE_URL}${link}`;
	}
};

// const data = {
//   id: "xxx",
//   quantity: 1,
//   name: "type",
//   pricing: {},
//   product: {
//     id: 'UHJvZHVjdDoxMw==',
//     name: 'Innovative PET Bottles: Recyclable and Stylish Solutions',
//     translation: {},
//     metadata: [],
//     slug: 'innovative-pet-bottles-recyclable-and-stylish-solutions'
//   }
// };
//获取本地的存储
export function getInquiry(): any {
	return JSON.parse(localStorage.getItem("inquiryData") || "[]");
}

export function setInquiry(product, count, VariantActiveName) {
	let locaInquiry = getInquiry();

	if (locaInquiry.length > 0) {
		let filterProduct = locaInquiry.filter((item) => item.variant.product.id == product.id)[0];

		if (filterProduct) {
			filterProduct.variant.product = product;
			filterProduct.quantity = filterProduct.quantity + count;
			message.success("add inquiry");
			return localStorage.setItem("inquiryData", JSON.stringify(locaInquiry));
		} else {
			const data = {
				quantity: count,

				variant: {
					name: VariantActiveName,
					product: product,
				},
			};
			locaInquiry.push(data);
			message.success("add inquiry");
			localStorage.setItem("inquiryData", JSON.stringify(locaInquiry));
		}
	} else {
		const data = {
			quantity: count,

			variant: {
				name: VariantActiveName,
				product: product,
			},
		};

		message.success("add inquiry");
		localStorage.setItem("inquiryData", JSON.stringify([data]));
	}

	// 手动触发 storage 事件
	const storageEvent = new Event("storage");
	window.dispatchEvent(storageEvent);

	// message.success("add inquiry");
}

export function deleteInquiryByProductId(productId) {
	let locaInquiry = getInquiry();

	// 查找包含特定商品ID的项
	const index = locaInquiry.findIndex((item) => item.variant.product.id === productId);

	if (index !== -1) {
		// 找到匹配的项后删除
		locaInquiry.splice(index, 1);

		// 更新本地存储数据
		localStorage.setItem("inquiryData", JSON.stringify(locaInquiry));

		// 手动触发 storage 事件
		const storageEvent = new Event("storage");
		window.dispatchEvent(storageEvent);

		message.success("Inquiry item deleted successfully");
	} else {
		message.error("Inquiry item not found");
	}
}
export let header = {
	username: "pintreel",
	token: "pt-6fgh52df466dsdfg",
};

export const shippingTitle = "center.f26a7189f413324e9058edcb137ad3de4be1";
